import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getProductsByCategoryId } from "../../utils/urls";
import { FiEye } from "react-icons/fi";
import ServerPaginationDataGrid from "../../components/common/CustomDataGrid/ServerPaginationDataGrid";
import GridListToggle from "../../components/common/GridListToggle";
import InfiniteScrollWrapper from "../../components/common/InfiniteScrollWrapper";

const ProductList = () => {
  const [viewMode, setViewMode] = useState(null);
  const { categoryId, subcategoryId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (window.innerWidth <= 600) {
      setViewMode("grid");
    } else {
      setViewMode("list");
    }
  }, []);

  const handleViewDetails = (productId) => {
    navigate(`/products/details/${productId}`); // Pass productId as URL param
  };

  const columns = [
    {
      field: "company_name",
      headerName: "Company & Plan",
      className: "max-sm:!min-w-[17rem]",
    },
    {
      field: "product_name",
      headerName: "Product",
      className: "max-sm:!min-w-[17rem]",
    },
    {
      field: "sub_category_title",
      headerName: "Sub-Category",
      className: "max-sm:!min-w-[17rem]",
    },
    {
      field: "description",
      headerName: "Description",
      className: "max-sm:!min-w-[20rem]",
    },

    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (row) => (
        <span
          className="flex items-center gap-1 text-[#0184FF] underline decoration-[#0184FF] text-sm cursor-pointer"
          onClick={() => handleViewDetails(row.product_id)}
        >
          <FiEye className="w-4 h-4 inline-block" />
          View
        </span>
      ),
    },
  ];

  if (!categoryId) {
    return <div className="p-6 text-gray-600">No category selected.</div>;
  }

function ProductCard({ data }) {
  const navigate = useNavigate();

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between h-full min-h-[18rem]"
      onClick={() => navigate(`/products/details/${data?.product_id}`)}
    >
      {/* Top: Company Logo + Name + Product Name */}
      <div>
        <div className="flex gap-3 items-start">
          <img
            src={data?.company_logo}
            alt="company-logo"
            className="w-12 h-12 object-contain rounded-md"
          />
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-gray-800 leading-snug">
              {data?.company_name}
            </p>
            <p className="text-sm font-medium text-gray-600 leading-tight">
              {data?.product_name}
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="mt-3">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Description:
          </p>
          <p className="text-sm text-gray-600 line-clamp-3">
            {data?.description || "No description available"}
          </p>
        </div>

        {/* Financial Description */}
        {data?.financial_description && (
          <div className="mt-2">
            <p className="text-sm font-semibold text-gray-700 mb-1">
              Financial Description:
            </p>
            <p className="text-sm text-gray-600 line-clamp-2">
              {data?.financial_description}
            </p>
          </div>
        )}
      </div>

      {/* View Details Button */}
      <div className="mt-4 flex items-center gap-1 text-blue-600 hover:underline text-sm font-medium">
        <FiEye className="w-4 h-4" />
        View Details
      </div>
    </div>
  );
}


  return (
    <div className="page-container flex-1">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold text-blue-600">List</h1>
        <div className="flex gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border h-full border-gray-300 rounded-md py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1116.65 16.65z"
              />
            </svg>
          </div>

          <GridListToggle viewMode={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {viewMode === "list" && (
        <ServerPaginationDataGrid
          url={getProductsByCategoryId}
          requestParameter={{
            // status: selectedStatus !== "Status" ? selectedStatus : null,
            category_id: categoryId,
            sub_category_id: subcategoryId || null,
            search: searchQuery || "",
          }}
          // search={search}
          limit={10}
          columns={columns}
          isServerPagination={true}
          dataMapper={(list) => list}
          height={"calc(100vh - 350px)"}
        />
      )}
      {viewMode === "grid" && (
        <InfiniteScrollWrapper
          url={getProductsByCategoryId}
          params={{
            // status: selectedStatus !== "Status" ? selectedStatus : null,
            category_id: categoryId,
            sub_category_id: subcategoryId || null,
            search: searchQuery || "",
          }}
          pageLimit={10}
          height="calc(100vh - 18rem)"
          scrollableDivClassName="max-h-[calc(100vh_-_18rem)]"
          ItemComp={ProductCard}
          gap={"1.5rem"}
          size={{ xxs: 12, xs: 12, md: 6, lg: 6 }}
        />
      )}
    </div>
  );
};

export default ProductList;
